import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react'

import {
  motion,
  useInView
} from 'framer-motion'

import YouTube from 'react-youtube'

import CustomImage from '../CustomImage'

import {
  StyledVideoPlayer,
  StyledVideoPlayerPreview,
  StyledVideoPlayerContent,
  StyledVideoPlayerPreviewIcon
} from './styles'

const inlineOpts = {
  rel: 0,
  controls: 1,
  disablekb: 1,
  iv_load_policy: 3,
  modestbranding: 1,
  mute: 0,
}

const videoVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
}

const previewVariants = {
  hidden: {
    opacity: 1, zIndex: 1
  },
  visible: {
    opacity: 0, zIndex: -1
  }
}

const VideoPlayer = ({
  videoId = null,
  poster,
  active = false,
  videoOpts,
  notClickable = false,
}) => {
  const ref = useRef(null)
  const player = useRef(null)

  const isInView = useInView(ref)
  const [status, setReady] = useState('hidden')

  const opts = useMemo(() => ({
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      ...inlineOpts,
      playlist: videoId,
      ...videoOpts,
    },
  }), [videoId])

  const _onReady = (event) => {
    player.current = event.target
    if (!active || !isInView) return
    event.target.playVideo()
    setReady('visible')
  }

  const _onEnd = () => {
    if (videoOpts && videoOpts.loop) return
    setReady('hidden')
    player.current.stopVideo()
  }

  useEffect(() => {
    if (player.current) {
      if (!isInView) {
        player.current.pauseVideo()
      } else {
        if (active) {
          player.current.playVideo()

          setReady('visible')
        }
      }
    }
  }, [isInView, active])

  return (
    <StyledVideoPlayer
      ref={ref}
      role="region"
      aria-label="Video player"
      aria-live="polite"
    >
      <StyledVideoPlayerPreview
        as={motion.div}
        initial="hidden"
        variants={previewVariants}
        animate={status}
        aria-hidden={status === 'visible'}
        role="img"
        aria-label={poster ? 'Immagine anteprima video' : 'Segnaposto video'}
      >
        {
          poster && <CustomImage data={poster} fill />
        }

        <StyledVideoPlayerPreviewIcon
          tabIndex={notClickable ? -1 : 0}
          handleClick={() => {
            setReady('visible')
            player.current.playVideo()
          }}
          iconStart="play"
          rounded
          aria-label="Riproduci video"
          aria-pressed={status === 'visible'}
          aria-describedby={videoId ? `video-description-${videoId}` : undefined}
        />
      </StyledVideoPlayerPreview>

      <StyledVideoPlayerContent
        as={motion.div}
        initial="hidden"
        animate={status}
        variants={videoVariants}
        aria-hidden={status === 'hidden'}
        role="application"
        aria-label="YouTube video player"
        title="Video content"
      >
        <YouTube
          videoId={videoId}
          opts={opts}
          onReady={_onReady}
          onEnd={_onEnd}
        />
      </StyledVideoPlayerContent>
    </StyledVideoPlayer>
  )
}

export default VideoPlayer
